// src/store/formsStore.ts
import { create } from "zustand";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Form, FormField, FormStatus } from "@/types/form";
import { useAuthStore } from "@/store/authStore";

interface FormsState {
  forms: Form[];
  loading: boolean;
  error: string | null;
  unsubscribe: (() => void) | null;

  init: () => void;
  cleanup: () => void;
  createForm: (data: {
    title: string;
    description?: string;
    fields?: FormField[];
  }) => Promise<string | null>;
  updateForm: (id: string, data: Partial<Form>) => Promise<boolean>;
  deleteForm: (id: string) => Promise<boolean>;
  setFormStatus: (id: string, status: FormStatus) => Promise<boolean>;
}

// Вспомогательная функция — убираем undefined
function cleanFields(fields: FormField[] = []) {
  return fields.map((f) => {
    const cleaned: any = {
      id: f.id,
      type: f.type,
      label: f.label,
      required: f.required ?? false,
    };
    if (f.placeholder) cleaned.placeholder = f.placeholder;
    if (f.helpText) cleaned.helpText = f.helpText;
    if (f.options && f.options.length > 0) cleaned.options = f.options;
    return cleaned;
  });
}

export const useFormsStore = create<FormsState>((set, get) => ({
  forms: [],
  loading: true,
  error: null,
  unsubscribe: null,

  init: () => {
    if (get().unsubscribe) return;

    set({ loading: true, error: null });

    const q = query(collection(db, "forms"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const forms: Form[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Form, "id">),
        }));
        set({ forms, loading: false });
      },
      (err) => {
        console.error("formsStore onSnapshot error:", err);
        set({ error: err.message, loading: false });
      }
    );

    set({ unsubscribe: unsub });
  },

  cleanup: () => {
    const unsub = get().unsubscribe;
    if (unsub) {
      unsub();
      set({ unsubscribe: null });
    }
  },

  createForm: async ({ title, description = "", fields = [] }) => {
    const user = useAuthStore.getState().user;
    if (!user) return null;

    try {
      const docRef = await addDoc(collection(db, "forms"), {
        title,
        description: description || "",
        status: "draft" as FormStatus,
        fields: cleanFields(fields),
        settings: {
          thankYouMessage: "Спасибо! Ваш отчёт принят.",
          allowMultiple: true,
        },
        createdBy: Number(user.id) || user.numericId || 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        responsesCount: 0,
      });

      return docRef.id;
    } catch (e) {
      console.error("createForm error:", e);
      return null;
    }
  },

  updateForm: async (id, data) => {
    try {
      const payload: any = {
        ...data,
        updatedAt: Timestamp.now(),
      };

      if (data.fields) {
        payload.fields = cleanFields(data.fields);
      }

      await updateDoc(doc(db, "forms", id), payload);
      return true;
    } catch (e) {
      console.error("updateForm error:", e);
      return false;
    }
  },

  deleteForm: async (id) => {
    try {
      await deleteDoc(doc(db, "forms", id));
      return true;
    } catch (e) {
      console.error("deleteForm error:", e);
      return false;
    }
  },

  setFormStatus: async (id, status) => {
    return get().updateForm(id, { status });
  },
}));